import { Text } from 'designSystem/components/Text'
import {
  DocumentUnavailableBody,
  DocumentUnavailableContainer,
  DocumentUnavailableId,
} from 'features/knowledgeDocumentViewer/components/knowledgeDocumentToolsStyles'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useDocumentConfigAvailability } from 'features/knowledgeDocumentViewer/hooks/useDocumentConfigAvailability'
import { FC, useCallback } from 'react'
import styled from 'styled-components'

const DebugCard = styled.div`
  width: 100%;
  max-width: 34rem;
  margin-top: 0.75rem;
  padding: 0.75rem 0.9rem;
  text-align: left;
  border: 1px solid ${(props) => props.theme.forecast.border};
  border-radius: 6px;
  background-color: ${(props) => props.theme.forecast.cardBackground};
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`

const DebugSectionLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(props) => props.theme.forecast.secondaryText};
`

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.8125rem;
  color: ${(props) => props.theme.forecast.defaultText};
`

const DetailKey = styled.span`
  color: ${(props) => props.theme.forecast.secondaryText};
  white-space: nowrap;
`

const DetailValue = styled.code`
  font-size: 0.75rem;
  color: ${(props) => props.theme.forecast.defaultText};
  word-break: break-all;
  text-align: right;
`

const ConfigList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

const ConfigRow = styled.li<{ $available: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: ${(props) =>
    props.$available
      ? props.theme.forecast.defaultText
      : props.theme.forecast.secondaryText};
`

const ConfigMark = styled.span<{ $available: boolean }>`
  font-weight: 600;
  color: ${(props) =>
    props.$available
      ? props.theme.forecast.primary
      : props.theme.forecast.secondaryText};
`

const ViewButton = styled.button`
  align-self: flex-start;
  margin-top: 0.15rem;
  padding: 0.45rem 0.85rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #fff;
  background-color: ${(props) => props.theme.forecast.primary};
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    filter: brightness(1.07);
  }
`

const Hint = styled.div`
  font-size: 0.75rem;
  color: ${(props) => props.theme.forecast.secondaryText};
`

const configLabel = (configId: string, status: string | undefined): string => {
  if (configId === 'legacy') return 'legacy (no pipeline config)'
  return status ? `${configId} · ${status}` : configId
}

interface DocumentUnavailablePanelProps {
  documentId: string
  siteID: string
}

/**
 * Shown when the viewer's exact-id lookup for the tab's document id resolves to
 * nothing in the selected pipeline config. External users get the plain "isn't
 * available" copy. Gecko-internal users additionally get diagnostics for the
 * pipeline-config scope mismatch that most often causes it — the site's
 * selected config, the config baked into the link (if any), and which configs
 * the document actually exists under — plus, when the document is present under
 * the current config as a differently-scoped id, a one-click jump to it.
 */
export const DocumentUnavailablePanel: FC<DocumentUnavailablePanelProps> = ({
  documentId,
  siteID,
}) => {
  const { selectedPipelineConfigId, isPreviewMode } = usePipelineConfig()
  const { activeTabId, updateDocumentHistoryItem, isGeckoUser } =
    useKnowledgeDocumentUXContext()
  // The detailed pipeline-config diagnostics are Gecko-internal only; external
  // users see just the plain unavailable message. Passing undefined disables
  // the probe so it fires no Rolodex queries for them.
  const { bareId, providedConfigId, perConfig, availableConfigs, isLoading } =
    useDocumentConfigAvailability(isGeckoUser ? documentId : undefined, siteID)

  const selectedConfigKey = selectedPipelineConfigId ?? 'legacy'
  const isAvailableUnderCurrent = availableConfigs.some(
    (config) => config.configId === selectedConfigKey
  )
  const canRescopeToCurrent = isAvailableUnderCurrent && bareId !== documentId

  const viewWithCurrentConfig = useCallback(() => {
    updateDocumentHistoryItem(activeTabId, {
      documentId: bareId,
      selectedBoxIds: [],
    })
  }, [activeTabId, bareId, updateDocumentHistoryItem])

  const otherAvailableConfigs = availableConfigs.filter(
    (config) => config.configId !== selectedConfigKey
  )

  return (
    <DocumentUnavailableContainer>
      <Text as='div' size='base' weight='semibold'>
        This document isn&apos;t available
      </Text>
      <DocumentUnavailableBody>
        The link may be broken, or the document may not be part of this
        site&apos;s current document set.
      </DocumentUnavailableBody>
      <DocumentUnavailableId>{documentId}</DocumentUnavailableId>

      {isGeckoUser && (
        <DebugCard>
          <DebugSectionLabel>Diagnostics</DebugSectionLabel>
          <DetailRow>
            <DetailKey>Current site config</DetailKey>
            <DetailValue>
              {selectedPipelineConfigId
                ? `${selectedPipelineConfigId}${isPreviewMode ? ' · preview' : ''}`
                : 'none (legacy site)'}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailKey>This link&apos;s config</DetailKey>
            <DetailValue>{providedConfigId ?? 'none (bare id)'}</DetailValue>
          </DetailRow>

          <DebugSectionLabel>Available under</DebugSectionLabel>
          {isLoading ? (
            <Hint>Checking this site&apos;s pipeline configs…</Hint>
          ) : perConfig.length === 0 ? (
            <Hint>No pipeline configs found for this site.</Hint>
          ) : (
            <ConfigList>
              {perConfig.map((config) => (
                <ConfigRow
                  key={config.configId}
                  $available={config.isAvailable}>
                  <span>{configLabel(config.configId, config.status)}</span>
                  <ConfigMark $available={config.isAvailable}>
                    {config.isAvailable ? '✓ available' : '— not present'}
                  </ConfigMark>
                </ConfigRow>
              ))}
            </ConfigList>
          )}

          {canRescopeToCurrent && (
            <>
              <ViewButton type='button' onClick={viewWithCurrentConfig}>
                View with current site config
              </ViewButton>
              <Hint>Opens {bareId}</Hint>
            </>
          )}
          {!isLoading &&
            !isAvailableUnderCurrent &&
            otherAvailableConfigs.length > 0 && (
              <Hint>
                This document exists only under other pipeline configs. Switch
                this site&apos;s active config to view it here.
              </Hint>
            )}
          {!isLoading && availableConfigs.length === 0 && (
            <Hint>
              This document isn&apos;t present under any of this site&apos;s
              pipeline configs — it may belong to a different site or have been
              re-ingested under a new id.
            </Hint>
          )}
        </DebugCard>
      )}
    </DocumentUnavailableContainer>
  )
}
